import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { GetContentQuery, useGetContentQuery } from "~/operations";
import { Space } from "~/lib/types";
import { useSearch } from "~/components/search/useSearch";
import { policyIcon } from "~/pages/inventory/utils/policyIcon";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { PolicyAssignButton } from "~/pages/security/policies/components/PolicyAssignButton";
import { Caption } from "~/pages/security/policies/components/Caption";
import { useFrameworkContext } from "~/pages/compliance/Framework/Framework";
import { useState } from "react";
import { setDocumentTitle } from "~/lib/commonUtils";

export type Policy = NonNullable<
  GetContentQuery["content"]
>["edges"][0]["node"];

export function FrameworkPolicies({}) {
  const { space, framework } = useFrameworkContext();
  const search = useSearch({ multiple: false });

  // Query for recommended policies (without search)
  const {
    data: { content: recommendedContent } = { content: null },
    refetch: refetchRecommended,
  } = useGetContentQuery({
    variables: {
      input: {
        scopeMrn: space.mrn,
        contentMrns: framework.policiesMrns,
        query: "",
      },
    },
    skip: framework.policiesMrns.length === 0,
  });

  // Query for active policies (with search)
  const {
    data: { content: activeContent } = { content: null },
    refetch: refetchActive,
  } = useGetContentQuery({
    variables: {
      input: {
        scopeMrn: space.mrn,
        contentMrns: framework.policiesMrns,
        query: search.searchFilters.map((v) => `"${v}"`).join(","),
      },
    },
    skip: framework.policiesMrns.length === 0,
  });

  const recommendedPolicies =
    recommendedContent?.edges
      ?.map((edge) => edge.node)
      .filter((policy) => !policy.assigned) || [];
  const activePolicies =
    activeContent?.edges
      ?.map((edge) => edge.node)
      .filter((policy) => policy.action) || [];

  const handleAssignChange: PolicyListProps["handleAssignChange"] = () => {
    refetchRecommended();
    refetchActive();
  };

  setDocumentTitle(["Policies", framework.name, "Compliance", space.name]);

  return (
    <Box>
      <PolicyList
        title="Recommended Policies"
        policies={recommendedPolicies}
        space={space}
        handleAssignChange={handleAssignChange}
      />
      <FilterBar
        searchId="compliance-policies"
        placeholder="compliance_policies"
        search={search}
      />
      <PolicyList
        title="Enabled Policies"
        policies={activePolicies}
        space={space}
        handleAssignChange={handleAssignChange}
        showExpandButton
      />
    </Box>
  );
}

type PolicyListProps = {
  title: string;
  policies: Policy[];
  space: Space;
  handleAssignChange: PolicyItemProps["handleAssignChange"];
  showExpandButton?: boolean;
};

const PolicyList = ({
  title,
  policies,
  space,
  handleAssignChange,
  showExpandButton = false,
}: PolicyListProps) => {
  if (!policies.length) return <Box />;

  const [showAll, setShowAll] = useState(false);

  // Determine how many policies to display
  const policiesToShow = showExpandButton
    ? showAll
      ? policies.length
      : 3 // Show 3 initially, all when expanded
    : policies.length; // Show all policies when no expand button

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <List component={Paper} sx={{ py: 0, overflow: "hidden" }}>
        {policies.slice(0, policiesToShow).map((policy) => {
          const policyId = policy.mrn?.split("/").pop();
          return (
            <PolicyItem
              key={policyId}
              policy={policy}
              space={space}
              handleAssignChange={handleAssignChange}
            />
          );
        })}

        {policies.length > 3 && showExpandButton && (
          <Box sx={{ textAlign: "center", my: 2 }}>
            <Button
              variant="text"
              onClick={() => setShowAll(!showAll)}
              size="small"
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: (theme) => theme.palette.secondary.light }}
            >
              {`${policies.length - 3} More Policies`}
            </Button>
          </Box>
        )}
      </List>
    </Box>
  );
};

type PolicyItemProps = {
  policy: Policy;
  space: Space;
  handleAssignChange: () => void;
};

const PolicyItem = ({ policy, space, handleAssignChange }: PolicyItemProps) => {
  const policyId = PolicyMrnToURIEncodedId(policy.mrn);
  const policyLink = `/space/findings/policies/${policyId}?spaceId=${space.id}`;
  const navigate = useNavigate();

  return (
    <ListItem
      sx={{
        p: 3,
        cursor: "pointer",
        "&:not(:last-child)": {
          borderBottom: (theme) =>
            `1px solid ${theme.palette.background.lighter}`,
        },
        "&:hover": {
          backgroundColor: (theme) => theme.palette.background.light,
        },
      }}
      onClick={() => navigate(policyLink)}
    >
      <Grid container spacing={0} size={12} sx={{ width: 1 }}>
        <Grid
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "left",
          }}
          size="auto"
        >
          {policyIcon(policy.name, "large")}
        </Grid>
        <Grid
          container
          size={{
            xs: 10,
            sm: "grow",
          }}
        >
          <Grid>
            <Typography variant="h6" component="h3" sx={{ pl: 3 }}>
              {policy.name}
            </Typography>
          </Grid>
          <Grid container spacing={0} size={12} sx={{ pl: 3 }}>
            <Caption
              trustLevel={policy.trustLevel}
              authors={policy.authors}
              version={policy.version}
            />
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "end",
          }}
          size={{
            sm: 2,
          }}
        >
          <PolicyAssignButton
            scope={space}
            policyMrn={policy.mrn}
            assigned={policy.assigned}
            action={policy.action}
            onChange={handleAssignChange}
            advanced
          />
        </Grid>
      </Grid>
    </ListItem>
  );
};
